import { useParams } from 'react-router-dom';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type { CountSessionDetailResponse } from '../../lib/types/inventory.js';
import { baseOrganizationPayloadOf, setupStepView, type SetupView } from '../../lib/types/setup.js';
import { isReady, PrintLoadState, PrintPageShell, TenantIdentityBlock } from './PrintChrome.js';

/**
 * Official Printable Physical Inventory Count Sheet (فرم رسمی شمارش انبارگردانی).
 * Conforms to standard internal audit and Iranian accounting standards:
 * - Legal entity header
 * - Session number, Warehouse identification, Jalali Date
 * - Blind/Non-blind stock count columns for 1st & 2nd counts
 * - Signature box for counters, warehouse keeper, and audit committee.
 */
export function CountSheetPrintPage() {
  const { slug, sessionId } = useParams<{ slug: string; sessionId: string }>();
  const base = slug ? tenantBasePath(slug) : '';

  const sessionDetail = useApiGet<CountSessionDetailResponse>(
    slug && sessionId ? `${base}/api/v1/inventory/count-sessions/${sessionId}` : null,
    [slug, sessionId],
  );
  const setup = useApiGet<SetupView>(slug ? `${base}/api/v1/setup` : null, [slug]);

  if (!slug || !sessionId) return null;
  const backHref = `${base}/inventory/count-sessions/${sessionId}`;

  if (!isReady(sessionDetail)) {
    return (
      <PrintPageShell backHref={backHref} backLabel="بازگشت به سشن انبارگردانی">
        <PrintLoadState
          error={sessionDetail.status === 'error' ? sessionDetail.error : undefined}
        />
      </PrintPageShell>
    );
  }

  const baseOrg = isReady(setup)
    ? baseOrganizationPayloadOf(setupStepView(setup.data, 'base-organization'))
    : null;

  const { session, lines } = sessionDetail.data;

  return (
    <PrintPageShell backHref={backHref} backLabel="بازگشت به سشن انبارگردانی">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 12 }}>
        {/* Top Header: Company Identity & Sheet Metadata */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 24,
            alignItems: 'flex-start',
          }}
        >
          <TenantIdentityBlock
            legalIdentity={baseOrg?.legalIdentity ?? null}
            primaryLocation={baseOrg?.primaryLocation ?? null}
            personType={baseOrg?.personType ?? null}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              alignItems: 'flex-end',
              textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 700, color: '#111' }}>
              برگه رسمی شمارش موجودی انبارگردانی
            </span>
            <span className="ltr sk-num" style={{ fontSize: 13, color: '#555' }}>
              شماره جلسه: {session.sessionNumber}
            </span>
            <span style={{ fontSize: 12, color: '#555' }}>
              تاریخ شمارش: {displayJalaliDate(session.sessionDate)}
            </span>
            <span style={{ fontSize: 12, color: '#555' }}>
              انبار: {session.warehouseName} ({session.warehouseCode})
            </span>
          </div>
        </div>

        {/* Count Sheet Instructions & Barcode Header */}
        <div
          style={{
            padding: '8px 12px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 11,
            color: '#475569',
          }}
        >
          <span>
            دستورالعمل: مقادیر شمارش‌شده را با خودکار آبی یا مشکی در ستون‌های مربوطه ثبت کنید.
            هرگونه خط‌خوردگی باید به امضای سرپرست شمارش برسد.
          </span>
          <span style={{ fontWeight: 600 }}>
            تعداد اقلام: {lines.length.toLocaleString('fa-IR')} قلم
          </span>
        </div>

        {/* Tabular Count Sheet */}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 11,
            marginTop: 8,
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
              <th style={{ padding: '6px 8px', textAlign: 'center', width: 36 }}>ردیف</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', width: 90 }}>کد کالا</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>نام و شرح کالا</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', width: 80 }}>محل انبار</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', width: 90 }}>بهر / بچ</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', width: 90 }}>سریال</th>
              <th style={{ padding: '6px 8px', textAlign: 'center', width: 75 }}>موجودی دفتری</th>
              <th
                style={{
                  padding: '6px 8px',
                  textAlign: 'center',
                  width: 75,
                  backgroundColor: '#fef3c7',
                }}
              >
                شمارش ۱
              </th>
              <th
                style={{
                  padding: '6px 8px',
                  textAlign: 'center',
                  width: 75,
                  backgroundColor: '#fef3c7',
                }}
              >
                شمارش ۲
              </th>
              <th
                style={{
                  padding: '6px 8px',
                  textAlign: 'center',
                  width: 75,
                  backgroundColor: '#dcfce7',
                }}
              >
                تأیید نهایی
              </th>
              <th style={{ padding: '6px 8px', textAlign: 'right', width: 100 }}>توضیحات فیزیکی</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, idx) => (
              <tr
                key={l.countLineId}
                style={{
                  borderBottom: '1px solid #e2e8f0',
                  height: 36,
                }}
              >
                <td style={{ padding: '4px 8px', textAlign: 'center', color: '#64748b' }}>
                  {(idx + 1).toLocaleString('fa-IR')}
                </td>
                <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontWeight: 600 }}>
                  {l.productCode}
                </td>
                <td style={{ padding: '4px 8px', fontWeight: 500 }}>{l.productName}</td>
                <td style={{ padding: '4px 8px', color: '#475569' }}>{l.locationCode}</td>
                <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 10 }}>
                  {l.lotCode ?? '—'}
                </td>
                <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 10 }}>
                  {l.serialNumber ?? '—'}
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 600 }}>
                  {BigInt(l.systemQty.numerator).toLocaleString('fa-IR')}
                </td>
                <td
                  style={{
                    padding: '4px 8px',
                    textAlign: 'center',
                    borderRight: '1px dashed #cbd5e1',
                  }}
                >
                  {l.countedQty ? BigInt(l.countedQty.numerator).toLocaleString('fa-IR') : ''}
                </td>
                <td
                  style={{
                    padding: '4px 8px',
                    textAlign: 'center',
                    borderRight: '1px dashed #cbd5e1',
                  }}
                ></td>
                <td
                  style={{
                    padding: '4px 8px',
                    textAlign: 'center',
                    borderRight: '1px solid #cbd5e1',
                    fontWeight: 700,
                  }}
                >
                  {l.countedQty ? BigInt(l.countedQty.numerator).toLocaleString('fa-IR') : ''}
                </td>
                <td style={{ padding: '4px 8px', fontSize: 10, color: '#64748b' }}>
                  {l.notes || ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* 4-Step Legal Authorized Signature Box */}
        <div
          style={{
            marginTop: 32,
            borderTop: '2px solid #0f172a',
            paddingTop: 16,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 16,
            textAlign: 'center',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
            <span style={{ fontWeight: 600, color: '#334155' }}>شمارش‌کننده اول</span>
            <div
              style={{
                borderTop: '1px dotted #94a3b8',
                paddingTop: 4,
                fontSize: 10,
                color: '#64748b',
              }}
            >
              نام و امضا
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
            <span style={{ fontWeight: 600, color: '#334155' }}>شمارش‌کننده دوم (کنترل)</span>
            <div
              style={{
                borderTop: '1px dotted #94a3b8',
                paddingTop: 4,
                fontSize: 10,
                color: '#64748b',
              }}
            >
              نام و امضا
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
            <span style={{ fontWeight: 600, color: '#334155' }}>مسئول انبار</span>
            <div
              style={{
                borderTop: '1px dotted #94a3b8',
                paddingTop: 4,
                fontSize: 10,
                color: '#64748b',
              }}
            >
              نام و امضا
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
            <span style={{ fontWeight: 600, color: '#334155' }}>نماینده حسابداری و حسابرسی</span>
            <div
              style={{
                borderTop: '1px dotted #94a3b8',
                paddingTop: 4,
                fontSize: 10,
                color: '#64748b',
              }}
            >
              نام و امضا
            </div>
          </div>
        </div>
      </div>
    </PrintPageShell>
  );
}
